import type { InsidedEmbedConfig } from './embed-widget.types'

export function getEmbedGlobal(): InsidedEmbedConfig {
  if (!window.__insidedEmbed) {
    window.__insidedEmbed = { communityUrl: '' }
  }
  return window.__insidedEmbed
}

interface CsrfDeferred {
  promise: Promise<string>
  resolve: (token: string) => void
  reject: (error: unknown) => void
}

let csrfDeferred: CsrfDeferred | null = null

function getCsrfDeferred(): CsrfDeferred {
  if (!csrfDeferred) {
    let resolveFn!: (token: string) => void
    let rejectFn!: (error: unknown) => void
    const promise = new Promise<string>((resolve, reject) => {
      resolveFn = resolve
      rejectFn = reject
    })
    // Suppress "unhandled rejection" warnings when no caller happens to await
    // (e.g. ensureResourcesLoaded called without an <gs-cc-embed-widget> mounted).
    // Consumers that DO await still receive the rejection through their own chain.
    promise.catch(() => {})
    csrfDeferred = { promise, resolve: resolveFn, reject: rejectFn }
  }
  return csrfDeferred
}

/**
 * Resolves with the CSRF token as soon as `/widget/resources` has been fetched and
 * parsed — earlier than awaiting `ensureResourcesLoaded()`, which also waits for
 * head + body script injection. Lets callers fire CSRF-dependent requests (like
 * `POST /widget/embed`) in parallel with remaining resource loading.
 */
export function awaitCsrfReady(): Promise<string> {
  const existing = getEmbedGlobal().csrfToken
  if (existing) return Promise.resolve(existing)
  return getCsrfDeferred().promise
}

const LANGUAGE_STORAGE_KEY = 'gs-cc-widget-lang'

/**
 * Resolves the effective widget language.
 *
 * Priority (highest to lowest):
 * 1. `?lang=` URL parameter — set by the language switcher on page reload;
 *    also persisted to localStorage so it survives subsequent visits.
 * 2. localStorage value — the user's previously chosen language.
 * 3. `window.__insidedEmbed.language` or `window.__insidedEmbed.context.language`
 *    — the host page's default (customers may set either).
 */
export function resolveLanguage(): string | undefined {
  const urlLang = new URLSearchParams(window.location.search).get('lang')
  if (urlLang) {
    persistLanguage(urlLang)
    return urlLang
  }

  const storedLang = getPersistedLanguage()
  if (storedLang) return storedLang

  const global = getEmbedGlobal()
  return global.language ?? (global.context?.language as string | undefined)
}

function persistLanguage(lang: string): void {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang)
  } catch {
    // localStorage may be unavailable (private browsing, storage quota, etc.)
  }
}

function getPersistedLanguage(): string | null {
  try {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY)
  } catch {
    return null
  }
}

/**
 * Emit <link rel="preload" as="script"> for every external <script src> in the batch.
 * Lets the browser download all scripts in parallel while we still inject <script> tags
 * sequentially — execution order is preserved (avoids breaking customer third-party
 * scripts or entrypoint dependencies), but network waits overlap.
 */
function preloadExternalScripts(elements: Element[]): void {
  for (const el of elements) {
    if (el.tagName !== 'SCRIPT') continue
    const src = el.getAttribute('src')
    if (!src) continue
    const link = document.createElement('link')
    link.rel = 'preload'
    link.as = 'script'
    link.setAttribute('href', src)
    const crossorigin = el.getAttribute('crossorigin')
    if (crossorigin !== null) link.setAttribute('crossorigin', crossorigin)
    document.head.appendChild(link)
  }
}

function loadScript(script: HTMLScriptElement, target: HTMLElement = document.body): Promise<void> {
  return new Promise((resolve, reject) => {
    const newScript = document.createElement('script')

    for (const attr of script.attributes) {
      newScript.setAttribute(attr.name, attr.value)
    }

    if (script.src) {
      newScript.onload = () => resolve()
      newScript.onerror = () => reject(new Error(`Failed to load script: ${script.src}`))
    } else {
      newScript.textContent = script.textContent
    }

    target.appendChild(newScript)

    if (!script.src) {
      resolve()
    }
  })
}

function isStylesheetLink(link: HTMLLinkElement): boolean {
  const rel = (link.getAttribute('rel') ?? '').trim().toLowerCase()
  const tokens = rel ? rel.split(/\s+/) : []
  return tokens.includes('stylesheet')
}

/** Resolve href to an absolute URL; returns null if empty or invalid. */
function resolveStylesheetUrl(href: string | null): string | null {
  if (href == null) return null
  const trimmed = href.trim()
  if (!trimmed) return null
  try {
    return new URL(trimmed).href
  } catch {
    console.error(`Invalid stylesheet URL: ${href}`)
    return null
  }
}

async function fetchWidgetResources(communityUrl: string): Promise<Document> {
  const params = new URLSearchParams()
  const productType = getEmbedGlobal().productType
  if (productType) params.set('productType', productType)

  const qs = params.toString()
  const url = qs ? `${communityUrl}/widget/resources?${qs}` : `${communityUrl}/widget/resources`

  const response = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'text/html' }
  })

  if (!response.ok) {
    throw new Error(`Failed to load widget resources: ${response.status}`)
  }

  const html = await response.text()
  return new DOMParser().parseFromString(html, 'text/html')
}

async function applyWidgetResources(doc: Document): Promise<void> {
  for (const el of Array.from(doc.head.children)) {
    await processHeadElement(el)
  }
}

function getHeadResources() {
  const global = getEmbedGlobal()
  if (!global.headResources) global.headResources = { stylesheets: [], styles: [] }
  return global.headResources
}

async function processHeadElement(el: Element): Promise<void> {
  if (el.tagName === 'SCRIPT') {
    try {
      await loadScript(el as HTMLScriptElement, document.head)
    } catch (error) {
      console.error('[widget-sdk] Failed to load head script:', error)
    }
    return
  }
  if (el.tagName === 'LINK' && el instanceof HTMLLinkElement && isStylesheetLink(el)) {
    const resolvedHref = resolveStylesheetUrl(el.getAttribute('href'))
    if (!resolvedHref) return
    const res = getHeadResources()
    if (res.stylesheets.some((s) => s.href === resolvedHref)) return
    const attributes: Record<string, string> = {}
    for (const attr of el.attributes) {
      if (attr.name === 'rel' || attr.name === 'href') continue
      attributes[attr.name] = attr.value
    }
    res.stylesheets.push({
      href: resolvedHref,
      attributes: Object.keys(attributes).length > 0 ? attributes : undefined
    })
    return
  }
  if (el.tagName === 'STYLE' && el instanceof HTMLStyleElement && el.textContent) {
    const res = getHeadResources()
    res.styles.push({ id: el.id || undefined, content: el.textContent })
  }
}

async function fetchWidgetResourcesBody(communityUrl: string): Promise<Document> {
  const response = await fetch(`${communityUrl}/widget/resourcesBody`, {
    method: 'GET',
    headers: { Accept: 'text/html' }
  })

  if (!response.ok) {
    throw new Error(`Failed to load widget body resources: ${response.status}`)
  }

  const html = await response.text()
  return new DOMParser().parseFromString(html, 'text/html')
}

async function applyWidgetResourcesBody(doc: Document): Promise<void> {
  for (const script of Array.from(doc.querySelectorAll('script'))) {
    try {
      await loadScript(script)
    } catch (error) {
      console.error('[widget-sdk] Failed to load body script:', error)
    }
  }
}

export async function ensureResourcesLoaded(communityUrl: string): Promise<void> {
  const global = getEmbedGlobal()

  if (global.loaded) return
  if (global.loading) return global.loading

  global.loading = (async () => {
    const [headResult, bodyResult] = await Promise.allSettled([
      fetchWidgetResources(communityUrl),
      fetchWidgetResourcesBody(communityUrl)
    ])

    // Signal CSRF availability the instant /resources is parsed so callers
    // (<gs-cc-embed-widget>) can fire /widget/embed in parallel with script injection.
    if (headResult.status === 'fulfilled') {
      const csrfInput = headResult.value.getElementById('csrftoken') as HTMLInputElement | null
      if (csrfInput?.value) {
        getEmbedGlobal().csrfToken = csrfInput.value
        getCsrfDeferred().resolve(csrfInput.value)
      } else {
        getCsrfDeferred().reject(new Error('CSRF token not found in /widget/resources response'))
      }
    } else {
      getCsrfDeferred().reject(headResult.reason)
    }

    // Kick off parallel downloads for every external script before we start
    // sequential injection — body preloads overlap with head execution.
    if (headResult.status === 'fulfilled') {
      preloadExternalScripts(Array.from(headResult.value.head.children))
    }
    if (bodyResult.status === 'fulfilled') {
      preloadExternalScripts(Array.from(bodyResult.value.querySelectorAll('script')))
    }

    if (headResult.status === 'fulfilled') {
      try {
        await applyWidgetResources(headResult.value)
      } catch (error) {
        console.error('[widget-sdk] Failed to apply head resources:', error)
      }
    } else {
      console.error('[widget-sdk] Failed to load head resources:', headResult.reason)
    }

    if (bodyResult.status === 'fulfilled') {
      try {
        await applyWidgetResourcesBody(bodyResult.value)
      } catch (error) {
        console.error('[widget-sdk] Failed to apply body resources:', error)
      }
    } else {
      console.error('[widget-sdk] Failed to load body resources:', bodyResult.reason)
    }

    global.loaded = true
  })()

  return global.loading
}

export function isResourcesLoaded(): boolean {
  return getEmbedGlobal().loaded === true
}
